import { forwardRef, ReactNode, ChangeEvent, FocusEvent, useState } from 'react'

import { cva, VariantProps } from 'class-variance-authority'

import { Eye, EyeSlash } from '@erudilabs/icons'

import { mergeClassName } from '@erudilabs/merge-class-utils'

const textInput = cva(
  [
    'border w-full transition-all h-10 rounded-md text-sm pl-4 pr-4 placeholder-gray-600 focus:outline-blue-500',
    'bg-white border-gray-300 text-black ',
    'dark:bg-gray-900 dark:border-gray-700 dark:text-white dark:focus:outline-blue-300',
  ],
  {
    variants: {
      disabled: {
        true: [
          'cursor-not-allowed',
          'bg-gray-200 border-gray-300 text-gray-600 hover:bg-gray-200 disabled:placeholder-gray-500',
          'dark:bg-gray-800 dark:border-gray-800 dark:text-gray-600 dark:hover:bg-gray-800 dark:disabled:placeholder-gray-700',
        ],
      },
    },
    defaultVariants: {
      disabled: false,
    },
  }
)

export interface TextInputProps extends VariantProps<typeof textInput> {
  className?: string
  testId?: string
  id?: string
  label?: string
  prefix?: ReactNode
  suffix?: ReactNode
  name: string
  helper?: ReactNode
  maxLength?: number
  defaultValue?: string
  value?: string
  readOnly?: boolean
  type?: 'text' | 'password' | 'email'
  placeholder?: string
  errorMessage?: string
  hideErrorMessage?: boolean
  clearable?: boolean
  autoFocus?: boolean
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: FocusEvent) => void
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (props, ref) => {
    const {
      type,
      readOnly,
      name,
      testId,
      id,
      onChange,
      onBlur,
      defaultValue,
      value,
      prefix,
      suffix,
      maxLength,
      clearable,
      errorMessage,
      autoFocus,
      hideErrorMessage,
      placeholder,
      helper,
      label,
      disabled,
      className,
    } = props
    const [showPassword, setShowPassword] = useState(false)
    const [showPasswordValue, setShowPasswordValue] = useState('password')

    return (
      <div className={mergeClassName('relative', className)}>
        {type === 'password' && (
          <div
            className={mergeClassName(
              label ? 'top-[38px]' : 'top-1/2 -translate-y-1/2',
              'absolute right-4 font-semibold text-gray-600 dark:text-gray-500'
            )}
          >
            {showPassword ? (
              <Eye
                className="text-blue-500 dark:text-blue-400 cursor-pointer"
                onClick={() => {
                  setShowPassword(!showPassword)
                  setShowPasswordValue('password')
                }}
              />
            ) : (
              <EyeSlash
                className="text-gray-600 dark:text-gray-500 cursor-pointer"
                onClick={() => {
                  setShowPassword(!showPassword)
                  setShowPasswordValue('text')
                }}
              />
            )}
          </div>
        )}
        {prefix && (
          <div
            className={mergeClassName(
              'absolute left-4',
              label ? 'top-[38px]' : 'top-1/2 -translate-y-1/2'
            )}
          >
            {prefix}
          </div>
        )}
        {suffix && (
          <div
            className={mergeClassName(
              'absolute right-4',
              label ? 'top-[38px]' : 'top-1/2 -translate-y-1/2'
            )}
          >
            {suffix}
          </div>
        )}
        {label && (
          <label
            htmlFor={name}
            className={mergeClassName(
              errorMessage
                ? 'text-red-600 dark:text-red-400'
                : 'text-black dark:text-white',
              'mb-2 block cursor-pointer text-left font-semibold'
            )}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          readOnly={readOnly}
          maxLength={maxLength}
          data-testid={testId}
          defaultValue={defaultValue}
          autoFocus={autoFocus}
          value={value}
          name={name}
          placeholder={placeholder}
          type={type === 'password' ? showPasswordValue : type || 'text'}
          className={mergeClassName(
            textInput({
              disabled,
            }),
            errorMessage && 'border-red-500 dark:border-red-400',
            type === 'password' && 'pr-11',
            suffix && 'pr-11',
            prefix && 'pl-11',
            clearable && 'pr-20'
          )}
          disabled={disabled as boolean}
          onChange={onChange}
          onBlur={onBlur}
          autoComplete="off"
        />
        {helper && (
          <p className="mt-2 text-left text-xs font-semibold text-gray-600 first-letter:capitalize">
            {helper}
          </p>
        )}
        {errorMessage && !hideErrorMessage && (
          <p className="mt-2 text-left text-xs font-semibold text-red-600 dark:text-red-400 first-letter:capitalize">
            {errorMessage}
          </p>
        )}
      </div>
    )
  }
)
